import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';

import { useIsMobile } from '~/core/hooks/useMediaQuery';
import { getProductionHeadRouteSkeleton } from '~/features/production_head/presentation/components/phScreenSkeletons';
import { useLayoutStore } from '~/shared/stores/layoutStore';

export function GlobalLoadingScreen() {
  const navigation = useNavigation();
  const location = useLocation();
  const isPendingNavigation = useLayoutStore((state) => state.isPendingNavigation);
  const [shouldRender, setShouldRender] = useState(true);
  const [opacity, setOpacity] = useState(1);
  const { t } = useTranslation(['common']);

  const isRouteChanging =
    navigation.state !== 'idle' &&
    navigation.location &&
    navigation.location.pathname !== location.pathname;

  const isInitialLoad = !location.key || location.key === 'default';
  const isActive = isInitialLoad || isPendingNavigation || isRouteChanging;

  // A Production Head screen shows its own skeleton instead of the generic spinner, both for a
  // client-side navigation to it and for the first document load of it. Other routes and
  // SecureLink pending keep the spinner. The last skeleton is kept while the overlay fades out so
  // it does not flash empty.
  const skeletonTarget =
    isPendingNavigation || !isActive
      ? null
      : isRouteChanging && navigation.location
        ? navigation.location
        : isInitialLoad
          ? location
          : null;
  const destinationSkeleton = skeletonTarget
    ? getProductionHeadRouteSkeleton(skeletonTarget.pathname, skeletonTarget.search)
    : null;
  const lastSkeletonRef = useRef<React.ReactNode>(null);
  if (destinationSkeleton) lastSkeletonRef.current = destinationSkeleton;
  const skeleton = destinationSkeleton ?? (isActive ? null : lastSkeletonRef.current);
  const showFullLoader = (isInitialLoad || isPendingNavigation || isRouteChanging) && !skeleton;

  const isSidebarOpen = useLayoutStore((state) => state.isSidebarOpen);
  const isMobile = useIsMobile();

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isActive) {
      setShouldRender(true);
      setOpacity(1);
    } else {
      setOpacity(0);
      timeout = setTimeout(() => {
        setShouldRender(false);
        lastSkeletonRef.current = null;
      }, 200);
    }

    return () => clearTimeout(timeout);
  }, [isActive]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col transition-opacity duration-200 ease-in-out ${
        skeleton ? 'overflow-y-auto' : 'items-center justify-center'
      } ${isActive ? '' : 'pointer-events-none'}`}
      style={{
        opacity,
        backgroundColor: '#f3f4f4',
        top: '80px', // Below the TopBar (h-20)
        left: isSidebarOpen && !isMobile ? '320px' : isMobile ? '0' : '96px',
      }}
    >
      {skeleton}

      {/* Only show spinner + text for initial load or SecureLink pending */}
      {showFullLoader && (
        <div className="relative flex flex-col items-center space-y-8">
          <div className="logo-pulse mb-2">
            <div className="windows-loader">
              <div />
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>
          <div className="text-center">
            <p className="bodyLg font-semibold tracking-wide text-primarySemiDark">
              {t('loadingContent')}
            </p>
            <p className="bodySm text-primarySemiDark/60 mt-1">{t('loadingContentDescription')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
